import React from "react";
import { Redirect, useLocation } from "react-router-dom";
import { useAppSelector } from "../../redux/typedHooks";
import { UserInterface } from "../../types/user";
import { USER_TYPE_IDS } from "../Header/ProfileViewer/profile-viewer";

const ALLOWED_PATHS = [
  "/apply-as-creator",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/choose-user-type",
  "/terms-of-service",
  "/privacy-policy",
  "/refund-policy",
  "/acceptable-use-policy",
  "/DMCA-takedown-policy",
  "/compliance-statement",
  "/kyc-aml-compliance-policy",
  "/cookie-policy",
  "/complaints-policy",
  "/content-provider-agreement",
  "/faq",
  "/help",
];

const CreatorOnboardingGuard: React.FC = () => {
  const location = useLocation();
  const reduxUser = useAppSelector<UserInterface | null>(
    (state) => state.user.currentUser
  );

  const token =
    localStorage.getItem("token") || localStorage.getItem("access_token");

  // Prefer localStorage user to avoid stale in-memory redux state when storage is cleared
  const user = (() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : reduxUser;
    } catch {
      return reduxUser;
    }
  })();

  // If no user is logged in, no token, or user is admin, allow navigation
  if (!user || !token || user.isAdmin) {
    return null;
  }

  // Always allow whitelisted authentication and legal policy routes
  const isAllowed = ALLOWED_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );

  if (isAllowed) {
    return null;
  }

  const rawUserType = user?.userType || (user as any)?.usertype || (user as any)?.user_type;
  const userTypeTitle = (typeof rawUserType === "string" ? rawUserType : rawUserType?.title)?.toLowerCase();
  const userTypeId = typeof rawUserType === "object" ? ((rawUserType as any)?._id || (rawUserType as any)?.id) : rawUserType;

  const isCreator =
    userTypeTitle === "creator" ||
    userTypeTitle === "model" ||
    userTypeId === USER_TYPE_IDS.CREATOR ||
    userTypeId === "6800a63485ec077c7c266c25" ||
    userTypeTitle === "6800a63485ec077c7c266c25" ||
    (user as any)?.contentProviderAgreementAccepted === true;

  // Only guard creators; other users are not governed by CreatorOnboardingGuard
  if (!isCreator) {
    return null;
  }

  const isEmailDone = user.emailVerified === true || (user as any).validated === true;
  const isPhoneDone = user.phoneVerified === true || Boolean(user.phoneNumber);
  const isProfileDone = user.profileCompletion === true || Boolean((user as any).aboutMe) || user.isOnboarding === true;

  const isCreatorComplete = isEmailDone && isPhoneDone && isProfileDone;

  const getRespectiveStep = (targetUser: any): number => {
    const emailDone = targetUser?.emailVerified === true || targetUser?.validated === true;
    const phoneDone = targetUser?.phoneVerified === true || Boolean(targetUser?.phoneNumber);
    const profileDone = targetUser?.profileCompletion === true || Boolean(targetUser?.aboutMe);

    if (!emailDone) return 1;
    if (!phoneDone) return 2;
    if (!profileDone) return 3;
    return 6;
  };

  console.log("[CreatorOnboardingGuard] Path check:", {
    pathname: location.pathname,
    isCreator,
    isCreatorComplete,
    emailVerified: user?.emailVerified,
    validated: (user as any)?.validated,
    phoneVerified: user?.phoneVerified,
    phoneNumber: user?.phoneNumber,
    profileCompletion: user?.profileCompletion,
    aboutMe: (user as any)?.aboutMe,
    isOnboarding: user?.isOnboarding,
  });

  // If creator has not completed email, phone, or profile completion, lock to creator onboarding
  if (!isCreatorComplete) {
    const step = getRespectiveStep(user);
    console.warn(`[CreatorOnboardingGuard] Creator onboarding incomplete -> Redirecting to /apply-as-creator?step=${step}`);
    return <Redirect to={`/apply-as-creator?step=${step}`} />;
  }

  // Fallback check for any creator whose email is explicitly unverified
  if (user.emailVerified === false && (user as any).validated !== true) {
    console.warn("[CreatorOnboardingGuard] Email unverified for creator -> Redirecting to /apply-as-creator?step=1");
    return <Redirect to="/apply-as-creator?step=1" />;
  }

  return null;
};

export default CreatorOnboardingGuard;
